import { useState, useEffect } from "react";
import { rtdb } from "../../services/firebase";
import { ChatChannel } from "../../types/db/chat";
import { WithID } from "../../types/db";
import { ChatChannelService } from "../../types/services/chat";

/** Channel info is the info of a channel with the channel id */
type ChannelInfo = WithID<ChatChannel>;

/**
 * Hook which takes care of fetching and updating the channel info of a chat
 * @param channelID The channel ID
 * @returns the channel info
 */
export const useChatChannel = (channelId: string): {
    /** The channel info */
    channel: ChannelInfo | null;
    /** If any action is currently loading */
    isLoading?: boolean;
    /** If any action ended up with an error */
    error?: string;
} => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [channel, setChannel] = useState<ChannelInfo | null>(null);

    useEffect(() => {
        if (!channelId) {
            //if no channelId no need to go farther
            setIsLoading(false);
            return;
        }
        //update the current channel with the channel info from BE
        setIsLoading(true);
        //subscribe to any child/info of that channel
        const fetchChannelAbout = (new ChatChannelService(rtdb)).getEntityRef(channelId)

        fetchChannelAbout
            .on("value", (snapshot) => {
                const channelAbout: ChatChannel | null = snapshot.val();
                if (!snapshot.exists() || !channelAbout) {
                    //can happen if doesn't exist
                    setError('Channel not found');
                    setChannel(null);
                    return;
                }
                setChannel({ ...channelAbout, id: channelId });
                setIsLoading(false);
            });

        return () => {
            //unsubscribe to all listener for the channel
            fetchChannelAbout.off();
            //reset the channel info
            setChannel(null);
        };
    }, [channelId]);

    useEffect(() => {
        //from the nature of the subscription, it is possible that the channel doesn't exist
        //so `setIsLoading(false)` never called, therefore we add a timeout
        setTimeout(() => setIsLoading(false), 1000)
    }, []);

    return {
        channel,
        isLoading,
        error,
    }
}